import { SpatialLevelAddUserUseCase } from "@admin/domain/usecases/spatial-level-add-user.usecase";
import { SpatialLevelAddUseCase } from "@admin/domain/usecases/spatial-level-add.usecase";
import { SpatialLevelDeleteUseCase } from "@admin/domain/usecases/spatial-level-delete.usecase";
import { SpatialLevelsResumeGetByStationIdUseCase } from "@admin/domain/usecases/spatial-levels-resume-get-by-station-id.usecase";
import { StationUpdateUseCase } from "@admin/domain/usecases/station-update.usecase";
import type { StationAdminFormValuesValidated } from "@admin/presentation/components/station-admin-form.view";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type { LatestData } from "@station/domain/models/latest-data.model";
import type { SpatialLevelsResume } from "@station/domain/models/spatial-level-resume.model";
import {
    SpatialLevel,
    SpatialLevels,
} from "@station/domain/models/spatial-level.model";
import type {
    Station,
    StationUpdate,
} from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { StationRemoveSelectedUseCase } from "@station/domain/usecases/station-remove-selected.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AdminPageViewmodel extends BaseViewModel {
    @observable
    spatialLevels: SpatialLevels = SpatialLevels.empty();

    @observable
    spatialLevel: Nullable<Readonly<SpatialLevel>> = null;

    @observable
    stations: Readonly<Station>[] = [];

    @observable
    selectedStations: readonly Readonly<Station>[] = [];

    @observable
    latestDataDictionary = new Map<number, LatestData[]>();

    @observable
    spatialLevelsResumeMap = new Map<string, SpatialLevelsResume>();

    @observable
    showEditSpatialLevelModal = false;

    @observable
    initialLoading = true;

    constructor(
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
        @inject(StationGetSelectedUseCase)
        private readonly stationGetSelectedUseCase: StationGetSelectedUseCase,
        @inject(StationRemoveSelectedUseCase)
        private readonly stationRemoveSelectedUseCase: StationRemoveSelectedUseCase,
        @inject(StationUpdateUseCase)
        private readonly stationUpdateUseCase: StationUpdateUseCase,
        @inject(SpatialLevelsResumeGetByStationIdUseCase)
        private readonly spatialLevelsResumeGetByStationIdUseCase: SpatialLevelsResumeGetByStationIdUseCase,
        @inject(SpatialLevelAddUseCase)
        private readonly spatialLevelAddUseCase: SpatialLevelAddUseCase,
        @inject(SpatialLevelDeleteUseCase)
        private readonly spatialLevelDeleteUseCase: SpatialLevelDeleteUseCase,
        @inject(SpatialLevelAddUserUseCase)
        private readonly spatialLevelAddUserUseCase: SpatialLevelAddUserUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override async didMount(): Promise<void> {
        await this.initViewData();
        this.addSub(
            this.stationGetSelectedUseCase.execute().subscribe((stations) => {
                void (async (): Promise<void> => {
                    runInAction(() => {
                        this.selectedStations = stations;
                    });

                    await this.fetchSpatialLevelsResumeForStations(stations);
                })();
            }),
        );
    }

    async initViewData(): Promise<void> {
        await this.fetchSpatialLevels();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    private async fetchSpatialLevels(): Promise<void> {
        this.spatialLevels = await this.spatialLevelGetAllUseCase.execute();
        runInAction(() => {
            this.stations = Array.from(this.spatialLevels.stationsMap.values());
        });
    }

    @action
    private async fetchSpatialLevelsResumeForStations(
        stations: readonly Readonly<Station>[],
    ): Promise<void> {
        const promises = stations.map(async (station) => {
            const resume =
                await this.spatialLevelsResumeGetByStationIdUseCase.execute(
                    station.id.value,
                );
            runInAction(() => {
                this.spatialLevelsResumeMap.set(station.id.value, resume);
            });
        });

        await Promise.all(promises);
    }

    @action
    async updateStation(
        values: StationAdminFormValuesValidated,
    ): Promise<Nullable<Station>> {
        LoadLayoutStore.start();

        const stationData: StationUpdate = {
            id: Number(values.id.value),
            name: values.name,
            technicalName: values.technicalName ?? null,
            latitude: values.latitude ?? null,
            longitude: values.longitude ?? null,
        };

        const stationUpdated = await this.stationUpdateUseCase.execute(
            values.id.value,
            stationData,
        );

        if (stationUpdated) {
            ToastManagerRepository.success();

            runInAction(() => {
                this.selectedStations = this.selectedStations.map((station) =>
                    station.id.value === stationUpdated.id.value
                        ? stationUpdated
                        : station,
                );
            });
        }

        LoadLayoutStore.finish();

        return stationUpdated;
    }

    @action
    removeStation(station: number): void {
        this.stationRemoveSelectedUseCase.execute(station);
        runInAction(() => {
            this.latestDataDictionary.delete(station);
        });
    }

    @action
    setSpatialLevel(spatialLevel: Readonly<SpatialLevel>): void {
        runInAction(() => {
            this.spatialLevel = spatialLevel;
        });
    }

    @action
    toggleShowEditSpatialLevelModal(): void {
        this.showEditSpatialLevelModal = !this.showEditSpatialLevelModal;
    }

    @action
    async reloadSpatialLevelsResume(): Promise<void> {
        await this.fetchSpatialLevelsResumeForStations(this.selectedStations);
    }

    @action
    async addSpatialLevelToStation(
        stationId: string,
        spatialLevelId: string,
    ): Promise<void> {
        const result = await this.spatialLevelAddUseCase.execute(
            stationId,
            spatialLevelId,
        );

        if (result) {
            await this.reloadSpatialLevelsResume();
        }
    }

    @action
    async deleteSpatialLevelToStation(
        stationId: string,
        spatialLevelId: string,
    ): Promise<void> {
        const result = await this.spatialLevelDeleteUseCase.execute(
            stationId,
            spatialLevelId,
        );

        if (result) {
            await this.reloadSpatialLevelsResume();
        }
    }

    @action
    async addUserToSpatialLevel(
        stationId: string,
        spatialLevelId: string,
        userIds: number[],
    ): Promise<void> {
        const result = await this.spatialLevelAddUserUseCase.execute(
            stationId,
            spatialLevelId,
            userIds,
        );

        if (result) {
            await this.reloadSpatialLevelsResume();
        }
    }
}
